import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { trackingService } from '../../../services/trackingService';

const NotificationPreferences = ({ userEmail }) => {
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadPreferences();
  }, [userEmail]);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      const prefs = await trackingService?.getNotificationPreferences(userEmail);
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveMessage('');
      await trackingService?.updateNotificationPreferences(userEmail, preferences);
      setSaveMessage('Préférences enregistrées avec succès');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSaveMessage('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Bell" className="text-primary" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Préférences de notification</h3>
            <p className="text-sm text-muted-foreground">Gérez vos alertes et notifications</p>
          </div>
        </div>

        {/* Proximity Alerts Section */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between pb-3 border-b border-border">
            <div>
              <h4 className="font-medium text-foreground">Alertes de proximité</h4>
              <p className="text-sm text-muted-foreground">Recevez des notifications quand le technicien approche</p>
            </div>
            <button
              onClick={() => handleToggle('enableProximityAlerts')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences?.enableProximityAlerts ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences?.enableProximityAlerts ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {preferences?.enableProximityAlerts && (
            <div className="space-y-3 pl-4 border-l-2 border-primary/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Alerte 15 minutes avant</span>
                </div>
                <button
                  onClick={() => handleToggle('enable15minAlert')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    preferences?.enable15minAlert ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      preferences?.enable15minAlert ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Alerte 10 minutes avant</span>
                </div>
                <button
                  onClick={() => handleToggle('enable10minAlert')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    preferences?.enable10minAlert ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      preferences?.enable10minAlert ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Alerte 5 minutes avant</span>
                </div>
                <button
                  onClick={() => handleToggle('enable5minAlert')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    preferences?.enable5minAlert ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      preferences?.enable5minAlert ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Status Updates Section */}
        <div className="space-y-4 mb-6">
          <h4 className="font-medium text-foreground pb-3 border-b border-border">Mises à jour de statut</h4>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Notification d'arrivée</span>
            </div>
            <button
              onClick={() => handleToggle('enableArrivalAlert')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                preferences?.enableArrivalAlert ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences?.enableArrivalAlert ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Notification de fin d'intervention</span>
            </div>
            <button
              onClick={() => handleToggle('enableCompletionAlert')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                preferences?.enableCompletionAlert ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences?.enableCompletionAlert ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Notification Channels Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground pb-3 border-b border-border">Canaux de notification</h4>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Mail" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Email</span>
            </div>
            <button
              onClick={() => handleToggle('enableEmail')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                preferences?.enableEmail ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences?.enableEmail ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Smartphone" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">SMS</span>
            </div>
            <button
              onClick={() => handleToggle('enableSms')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                preferences?.enableSms ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences?.enableSms ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Notifications push</span>
            </div>
            <button
              onClick={() => handleToggle('enablePush')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                preferences?.enablePush ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences?.enablePush ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            {saveMessage && (
              <span className={`text-sm ${
                saveMessage?.includes('succès') ? 'text-success' : 'text-destructive'
              }`}>
                {saveMessage}
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="ml-auto"
            >
              {isSaving ? (
                <>
                  <Icon name="Loader2" className="animate-spin mr-2" size={16} />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Icon name="Save" className="mr-2" size={16} />
                  Enregistrer les préférences
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      {/* Info Card */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" className="text-primary flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-foreground">
            <p className="font-medium mb-1">À propos des alertes de proximité</p>
            <p className="text-muted-foreground">
              Les alertes de proximité vous informent automatiquement lorsque votre technicien approche de votre domicile. 
              Vous recevrez des notifications à 15, 10 et 5 minutes avant son arrivée estimée.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;